import { ClientAlgorithm } from '../enums/client-algorithm.enum';
import { ClientScope } from '../enums/client-scope.enum';
import { ClientStatus } from '../enums/client-status.enum';
import { ClientType } from '../enums/client-type.enum';
import { NameIdType } from '../enums/name-id-type.enum';
import { BackEndDirectory, Directory } from './directory';

export abstract class Client {
  public id: string;
  public type: ClientType;
  public status: ClientStatus;
  public algorithm: ClientAlgorithm;
  public logoutRedirectUri: string;

  constructor(backEndClient?: BackEndClient) {
    if (backEndClient) {
      this.id = backEndClient.id;
      this.type = backEndClient.type;
      this.status = backEndClient.status;
      this.algorithm = backEndClient.algorithm;
      this.logoutRedirectUri = backEndClient.logout_redirect_uri;
    }
  }

  public toBackEnd(): BackEndClient {
    return {
      id: this.id,
      type: this.type,
      status: this.status,
      algorithm: this.algorithm,
      logout_redirect_uri: this.logoutRedirectUri,
    };
  }

  protected static dateOrUndefined(date: string) {
    return date ? new Date(Date.parse(date)) : undefined;
  }

  protected static isoOrUndefined(date: Date) {
    return date ? date.toISOString() : null;
  }
}

export class OauthClient extends Client {
  public secret: string;
  public scopes: ClientScope[];
  public redirectUri: string[];
  public publicClient: boolean;
  public includeUserInfo: boolean;

  constructor(backendClient?: BackEndOauthClient) {
    super(backendClient);
    if (!backendClient) {
      return;
    }
    if (backendClient.type !== ClientType.OAuth2) {
      throw new Error(`Cannot instantiate OauthClient with type: ${backendClient.type}`);
    }
    this.secret = backendClient.secret;
    this.scopes = backendClient.scopes;
    this.redirectUri = backendClient.redirect_uri;
    this.publicClient = backendClient.public_client;
    this.includeUserInfo = backendClient.include_user_info;
  }

  public toBackEnd(): BackEndOauthClient {
    return {
      ...super.toBackEnd(),
      secret: this.secret,
      scopes: this.scopes,
      redirect_uri: this.redirectUri,
      public_client: this.publicClient,
      include_user_info: this.includeUserInfo,
    } as BackEndOauthClient;
  }
}

export class SamlClient extends Client {
  public spEntityId: string;
  public idpEntityId: string;
  public nameIdType: NameIdType;
  public attributeProfile: string;
  public acsUri: string[];
  public spCertificate: string;
  public spCertExpires: Date;
  public idpCertificate: string;
  public idpCertExpires: Date;
  public directory?: Directory;

  constructor(backendClient?: BackEndSamlClient) {
    super(backendClient);
    if (!backendClient) {
      return;
    }
    if (backendClient.type !== ClientType.SAML2) {
      throw new Error(`Cannot instantiate SamlClient with type: ${backendClient.type}`);
    }
    this.spEntityId = backendClient.sp_entity_id;
    this.idpEntityId = backendClient.idp_entity_id;
    this.nameIdType = backendClient.name_id_type;
    this.attributeProfile = backendClient.attribute_profile;
    this.acsUri = backendClient.acs_uri;
    this.spCertificate = backendClient.sp_certificate;
    this.spCertExpires = Client.dateOrUndefined(backendClient.sp_cert_expires);
    this.idpCertificate = backendClient.idp_certificate;
    this.idpCertExpires = Client.dateOrUndefined(backendClient.idp_cert_expires);
    this.directory = new Directory(backendClient.directory);
  }

  public toBackEnd(): BackEndSamlClient {
    return {
      ...super.toBackEnd(),
      sp_entity_id: this.spEntityId,
      idp_entity_id: this.idpEntityId,
      name_id_type: this.nameIdType,
      attribute_profile: this.attributeProfile,
      acs_uri: this.acsUri,
      sp_certificate: this.spCertificate,
      sp_cert_expires: Client.isoOrUndefined(this.idpCertExpires),
      idp_certificate: this.idpCertificate,
      idp_cert_expires: Client.isoOrUndefined(this.idpCertExpires),
      directory: this.directory?.toBackend(),
    } as BackEndSamlClient;
  }
}

export interface BackEndClient {
  id: string;
  type: ClientType;
  status: ClientStatus;
  algorithm: ClientAlgorithm;
  logout_redirect_uri?: string;
}

export interface BackEndOauthClient extends BackEndClient {
  secret?: string;
  scopes: ClientScope[];
  redirect_uri: string[];
  public_client: boolean;
  include_user_info: boolean;
}

export interface BackEndSamlClient extends BackEndClient {
  sp_entity_id: string;
  idp_entity_id: string;
  name_id_type: NameIdType;
  attribute_profile: string;
  acs_uri: string[];
  sp_certificate: string;
  sp_cert_expires?: string;
  idp_certificate?: string;
  idp_cert_expires?: string;
  directory?: BackEndDirectory;
}
